import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RecordActionsMenu } from "@/components/RecordActions";
import { FileStack, ShieldCheck, CheckCircle2, AlertTriangle, RefreshCw, Eye } from "lucide-react";

const TSTYLE = { "Imported": "bg-slate-100 text-slate-600", "Draft": "bg-blue-100 text-blue-800", "Internally Reviewed": "bg-indigo-100 text-indigo-800", "Approved": "bg-emerald-100 text-emerald-800", "Archived": "bg-slate-100 text-slate-500" };

export default function PolicyTemplateLibrary({ me }) {
  const [templates, setTemplates] = useState([]);
  const [detail, setDetail] = useState(null);
  const isSuper = me?.recon_role === "super_admin";
  const canEdit = me?.recon_role === "super_admin" || me?.recon_role === "compliance_admin";

  const load = useCallback(() => { api.get("/policy/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const reimport = async () => {
    try { const r = await api.post("/policy/import"); toast.success(`Imported ${r.data.templates_imported} templates, ${r.data.fields_seeded} fields.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  const setStatus = async (t, status) => {
    try { await api.post(`/policy/templates/${t.key}/review`, { status }); toast.success(`${t.title} → ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <section className="rounded-md border border-border bg-card" data-testid="policy-template-library">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2"><FileStack className="h-4 w-4 text-gold-dark" /><h2 className="font-display font-bold text-navy">Policy Template Library</h2></div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"><ShieldCheck className="h-4 w-4" /> {isSuper ? "Super Admin" : canEdit ? "Compliance Admin" : "Read-only"}</span>
          {isSuper && <Button size="sm" variant="outline" onClick={reimport} data-testid="policy-reimport"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-run Import</Button>}
        </div>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-sm text-muted-foreground">Master DOCX templates power the Policy &amp; Procedure Generator. Originals are preserved immutably in object storage; a replacement creates a new version. Templates are imported as Draft/Imported and only move to Internally Reviewed after a reviewer confirms formatting, field mapping, and a sample generation.</p>
        <div className="rounded border border-border overflow-hidden">
          <div className="overflow-x-auto max-h-[26rem] overflow-y-auto"><table className="w-full text-sm">
            <thead className="sticky top-0"><tr className="bg-slate-50 border-b text-left">{["Template", "Category", "Ver", "Fields", "Sample", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.key} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid="policy-tpl-row">
                  <td className="px-3 py-2.5 font-medium text-navy">{t.title}</td>
                  <td className="px-3 py-2.5 text-xs">{t.category}</td>
                  <td className="px-3 py-2.5 text-xs">{t.version}</td>
                  <td className="px-3 py-2.5 text-xs">{t.placeholders?.length || 0}</td>
                  <td className="px-3 py-2.5">{t.sample_ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}</td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${TSTYLE[t.status] || "bg-slate-100"}`}>{t.status}</span></td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-0.5">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDetail(t)} data-testid={`policy-tpl-view-${t.key}`}><Eye className="h-4 w-4" /></Button>
                      {canEdit && t.status !== "Internally Reviewed" && t.status !== "Approved" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "Internally Reviewed")} data-testid={`policy-tpl-review-${t.key}`}>Mark Reviewed</Button>}
                      {isSuper && t.status === "Internally Reviewed" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "Approved")}>Approve</Button>}
                      <RecordActionsMenu type="policy_template" id={t.id} onChanged={load} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
      {detail && (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="policy-tpl-detail">
            <DialogHeader><DialogTitle>{detail.title}</DialogTitle><DialogDescription>{detail.filename} · v{detail.version} · {detail.status}</DialogDescription></DialogHeader>
            <div className="text-sm space-y-2">
              <div className="text-xs text-muted-foreground">Detected placeholders ({detail.placeholders?.length})</div>
              <div className="flex flex-wrap gap-1.5">{(detail.placeholders || []).map((p) => <span key={p} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-mono">{p}</span>)}</div>
              <div className="text-xs text-muted-foreground pt-2">Validation: opens={String(detail.validation?.opens)} · replaced={detail.validation?.replaced} · tables={detail.validation?.tables} · raw left={detail.validation?.raw_left?.length || 0}</div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
